
import React from 'react';
import { ArrowLeft, MessageSquare, ZapIcon, Share } from 'lucide-react';

const ScenarioExplorer = ({ scenario, onBack, currentPage }) => {
  // Mock scenario data based on context
  const getScenarioData = (title, page) => {
    // In a real implementation, this would be dynamic based on API calls or more complex logic
    const scenarioMap = {
      'Competitor price strategy changes': {
        insight: "Recent competitor price reductions have led to a 2.3% market share shift in affected regions.",
        impact: "Medium-High",
        timeframe: "3-6 months",
        affectedSegments: ["Value shoppers", "Fleet customers"],
        recommendations: [
          "Implement targeted price matching in key areas",
          "Enhance loyalty benefits to increase retention",
          "Review cost structure to enable competitive pricing"
        ]
      },
      'Rush hour behavior changes': {
        insight: "Morning rush hour customers now arrive 15 minutes earlier on average compared to last year.",
        impact: "Medium",
        timeframe: "Immediate",
        affectedSegments: ["Commuters", "Business travelers"],
        recommendations: [
          "Adjust staffing patterns to match new peak times",
          "Create early bird promotions for coffee/breakfast items",
          "Ensure forecourt is fully ready earlier in the morning"
        ]
      },
      'Customer segment price sensitivity': {
        insight: "Premium segment shows 40% less price sensitivity than value segment in urban locations.",
        impact: "High",
        timeframe: "Ongoing",
        affectedSegments: ["Premium customers", "Urban locations"],
        recommendations: [
          "Implement segment-based pricing strategy",
          "Bundle premium fuels with higher-margin convenience items",
          "Create tiered loyalty rewards based on purchase patterns"
        ]
      },
      // Default fallback for other scenarios
      'default': {
        insight: "Analysis shows significant correlation between this factor and overall performance metrics.",
        impact: "Medium",
        timeframe: "1-3 months",
        affectedSegments: ["Multiple segments"],
        recommendations: [
          "Further analyze data patterns for actionable insights",
          "Test targeted interventions in select locations",
          "Monitor results and scale successful approaches"
        ]
      }
    };
    
    // Find exact match or return default
    return scenarioMap[title] || scenarioMap['default'];
  };
  
  const scenarioData = getScenarioData(scenario.title, currentPage);
  
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <button 
          onClick={onBack}
          className="text-sm text-gray-600 flex items-center hover:text-gray-900"
        >
          <ArrowLeft size={14} className="mr-1" /> Back to Analysis
        </button>
        <span className="text-xs text-purple-700 bg-purple-100 px-2 py-1 rounded-full">
          Scenario Explorer
        </span>
      </div>
      
      <div className="mt-2">
        <h4 className="font-medium text-gray-800">{scenario.title}</h4>
        <div className="mt-4 space-y-4">
          <div className="bg-purple-50 p-3 rounded-md">
            <div className="flex items-start">
              <MessageSquare size={16} className="text-purple-600 mt-1 mr-2 flex-shrink-0" />
              <p className="text-sm text-gray-800">{scenarioData.insight}</p>
            </div>
          </div>
          
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-gray-50 p-3 rounded-md">
              <p className="text-xs text-gray-500">Impact</p>
              <p className="text-sm font-medium text-gray-800">{scenarioData.impact}</p>
            </div>
            <div className="bg-gray-50 p-3 rounded-md">
              <p className="text-xs text-gray-500">Timeframe</p>
              <p className="text-sm font-medium text-gray-800">{scenarioData.timeframe}</p>
            </div>
            <div className="bg-gray-50 p-3 rounded-md">
              <p className="text-xs text-gray-500">Confidence</p>
              <p className="text-sm font-medium text-gray-800">85%</p>
            </div>
          </div>
          
          <div>
            <p className="text-xs text-gray-500 mb-2">Affected Segments</p>
            <div className="flex flex-wrap gap-2">
              {scenarioData.affectedSegments.map((segment, index) => (
                <span 
                  key={index} 
                  className="text-xs bg-gray-100 text-gray-800 px-2 py-1 rounded-full"
                >
                  {segment}
                </span>
              ))}
            </div>
          </div>
          
          <div>
            <div className="flex items-center">
              <ZapIcon size={14} className="text-purple-600 mr-2" />
              <p className="text-sm font-medium text-gray-800">Recommended Actions</p>
            </div>
            <ul className="mt-2 space-y-2">
              {scenarioData.recommendations.map((rec, index) => (
                <li key={index} className="text-sm text-gray-700 flex items-start">
                  <span className="inline-block w-4 h-4 bg-purple-100 rounded-full mr-2 mt-1 flex-shrink-0"></span>
                  {rec}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
      
      <div className="mt-4 flex justify-end">
        <button className="text-xs flex items-center text-gray-600 hover:text-gray-900">
          <Share size={12} className="mr-1" /> Share insights
        </button>
      </div>
    </div>
  );
};

export default ScenarioExplorer;
